import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import toArray from 'lodash/toArray'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { FORMAT } from 'modules/core/lib/dates'
import { Model } from 'modules/core/lib/mobx-rest'

import { NumericFieldTypes } from 'modules/custom_resources/store/collections/customFields/fields'

export interface Question {
  category_id?: number
  category_name?: string
  id: number
  label: string
  max_value?: number
  min_value?: number
  options?: string[]
  position: number
  type: string
}

export interface Answer {
  question_id: number
  value: string | number
}
export interface Answers {
  [questionId: number]: string | number
}

export interface QuestionGroups {
  answered_at: string
  created_at: string
  end_on: string
  id: number
  participant_id: number | null
  respondent_first_name: string
  respondent_last_name: string
}
export interface IAnalyticQuestionGroup extends QuestionGroups {
  answers: Answer[]
}
export interface AnalyticQuestionGroup extends QuestionGroups {
  answers: Answers
}

interface Category {
  name: string
  questionsIds: Set<number>
}

export type Cycle = [string, string]
const NONE_CATEGORY = 0

interface IAnalyticResult {
  id: number
  question_groups: IAnalyticQuestionGroup[]
  questions: Question[]
  type_of_reviewer: string
}

class AnalyticResult extends Model<IAnalyticResult> {
  constructor(attr = {} as IAnalyticResult) {
    super(attr)
    makeObservable(this, {
      typeOfReviewer: computed,
      questionGroups: computed,
      questions: computed,
      questionIds: computed,
      categories: computed,
      numericQuestions: computed,
      numericQuestionIds: computed,
      numericCategories: computed,
      cycles: computed,
    })
  }

  static urlRoot() {
    return '/enps/analytics'
  }

  get typeOfReviewer(): string {
    return this.attributes.type_of_reviewer
  }

  get questions(): Set<Question> {
    const groupedQuestions = groupBy(
      this.attributes.questions,
      (q) => q.category_id || NONE_CATEGORY
    )
    const questions = toArray(groupedQuestions).flatMap((questions) =>
      sortBy(questions, 'position')
    )

    return new Set(questions)
  }

  get questionIds(): Set<number> {
    return new Set(Array.from(this.questions).map((q: Question) => q.id))
  }

  get numericQuestions(): Set<Question> {
    const numericFuildTypes = Object.values(NumericFieldTypes) // eslint-disable-line
    const numericQuestions = new Set<Question>()

    this.questions.forEach((question: Question) => {
      if (numericFuildTypes.includes(question.type)) {
        numericQuestions.add(question)
      }
    })

    return numericQuestions
  }

  get numericQuestionIds(): Set<number> {
    return new Set(Array.from(this.numericQuestions).map((q: Question) => q.id))
  }

  get questionGroups(): AnalyticQuestionGroup[] {
    const { question_groups } = this.attributes

    return question_groups.map((questionGroup: IAnalyticQuestionGroup) => {
      const { answers: questionGroupAnswers } = questionGroup

      const answers = questionGroupAnswers.reduce((memo: Answers, answer: Answer) => {
        memo[answer.question_id] = answer.value

        return memo
      }, {})

      return {
        ...questionGroup,
        answers,
      }
    })
  }

  get categories(): Record<number, Category> {
    return this.groupCategories(this.questions)
  }

  get numericCategories(): Record<number, Category> {
    return this.groupCategories(this.numericQuestions)
  }

  groupCategories(questions: Set<Question>): Record<number, Category> {
    const memo: Record<number, Category> = {}

    questions.forEach((question: Question) => {
      const categoryId = question.category_id || NONE_CATEGORY

      if (!memo[categoryId]) {
        memo[categoryId] = {
          name: question.category_name || '',
          questionsIds: new Set([question.id]),
        }
      }

      memo[categoryId]?.questionsIds.add(question.id)
    })

    return memo
  }

  get cycles(): Set<Cycle> {
    return new Set(
      this.attributes.question_groups.map((questionGroup: IAnalyticQuestionGroup) => [
        questionGroup.created_at,
        questionGroup.end_on || moment().format(FORMAT.backend),
      ])
    )
  }

  questionGroupsInCycle(cycle: Cycle | null): AnalyticQuestionGroup[] {
    if (!cycle) return this.questionGroups

    return this.questionGroups.filter(({ created_at, end_on }: AnalyticQuestionGroup) => {
      const [created, end] = cycle

      return (end_on === end || !end_on) && created_at == created
    })
  }
}

export default AnalyticResult
